import { useMemo, useCallback, useRef } from 'react'
import type { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import type {
    MarkingEditorState,
    MarkingPosition,
    TextAlignment,
    FontFamily,
    ExtendedMarkingFormData,
} from '../types'
import { useMarkingInitialization } from './useMarkingInitialization'
import { useMarkingState } from './useMarkingState'
import { useMarkingPersistence } from './useMarkingPersistence'
import { useMarkingChanges } from './useMarkingChanges'
import { useMarkingScale } from './useMarkingScale'
import type { MarkingEditorScale } from '../utils'

/**
 * Параметры для хука-композиции редактора маркировки
 */
export interface UseMarkingEditorParams {
    /**
     * Дескриптор рабочего пространства и ссылки
     */
    wwld: WorkspaceWithLinkDescriptor
    /**
     * Начальные данные из пропсов
     */
    initialData?: Partial<MarkingEditorState>
    /**
     * Открыто ли модальное окно (для загрузки данных)
     */
    isOpen?: boolean
}

/**
 * Тип возвращаемого значения хука useMarkingEditor
 */
export interface UseMarkingEditorReturn {
    // Состояние
    state: MarkingEditorState
    initialState: MarkingEditorState
    hasChanges: boolean

    // Сеттеры состояния
    setMarkMedia: (value: boolean) => void
    setMarkMediaColor: (value: string) => void
    setMarkMediaText: (value: string | null) => void
    setFontSize: (value: number) => void
    setAlignment: (value: TextAlignment) => void
    setFontFamily: (value: FontFamily) => void
    setHasBackground: (value: boolean) => void
    setBackgroundColor: (value: string | null) => void
    setPosition: (value: MarkingPosition | null) => void
    updateState: (updates: Partial<MarkingEditorState>) => void
    reset: () => void
    resetToCurrentData: () => void

    // Масштаб
    scale: MarkingEditorScale | null
    handleScaleChange: (scale: MarkingEditorScale) => void
    getCurrentScale: () => MarkingEditorScale | null

    // Сохранение и загрузка
    save: (customTextColors?: string[], customBackgroundColors?: string[]) => void
    savedData: ExtendedMarkingFormData | null | undefined
    isLoading: boolean
    isFetching: boolean
    isSaving: boolean
}

/**
 * Хук-композиция для редактора маркировки
 * Объединяет все хуки редактора и предоставляет единый интерфейс
 */
export function useMarkingEditor(
    params: UseMarkingEditorParams
): UseMarkingEditorReturn {
    const { wwld, initialData, isOpen = false } = params

    // Загружаем данные из БД
    const persistence = useMarkingPersistence(wwld, isOpen)

    // Загружаем расширенные настройки из localStorage (временное решение)
    // Мемоизируем, чтобы избежать лишних пересчетов
    // Используем savedData и loadMarkingSettings напрямую для стабильности зависимостей
    const localData = useMemo(() => {
        try {
            return persistence.loadMarkingSettings()
        } catch (error) {
            return null
        }
        // Используем savedData для отслеживания изменений вместо всего объекта persistence
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [persistence.savedData, persistence.loadMarkingSettings])

    // Инициализация состояния
    const initialState = useMarkingInitialization({
        initialData,
        savedData: persistence.savedData ?? undefined,
        localData: localData ?? undefined,
    })

    // Управление состоянием
    const markingState = useMarkingState({
        initialData,
        savedData: persistence.savedData ?? undefined,
        localData: localData ?? undefined,
        initialState,
    })

    // Отслеживание изменений
    const hasChanges = useMarkingChanges({
        currentState: markingState.state,
        initialState: markingState.initialState,
    })

    // Управление масштабом
    const { scale, handleScaleChange, getCurrentScale } = useMarkingScale({
        savedData: persistence.savedData ?? undefined,
        localData: localData ?? undefined,
        isOpen,
    })

    /**
     * Сохранение настроек маркировки
     * Мемоизируем для стабильности ссылки
     * Используем ref для state, чтобы избежать лишних пересозданий функции
     */
    const stateRef = useRef(markingState.state)
    stateRef.current = markingState.state

    const save = useCallback(
        (
            customTextColors?: string[],
            customBackgroundColors?: string[]
        ) => {
            const currentScale = getCurrentScale()
            persistence.saveMarkingSettings(
                stateRef.current,
                currentScale,
                customTextColors,
                customBackgroundColors
            )
        },
        [getCurrentScale, persistence.saveMarkingSettings]
    )

    return {
        // Состояние
        state: markingState.state,
        initialState: markingState.initialState,
        hasChanges,

        // Сеттеры состояния
        setMarkMedia: markingState.setMarkMedia,
        setMarkMediaColor: markingState.setMarkMediaColor,
        setMarkMediaText: markingState.setMarkMediaText,
        setFontSize: markingState.setFontSize,
        setAlignment: markingState.setAlignment,
        setFontFamily: markingState.setFontFamily,
        setHasBackground: markingState.setHasBackground,
        setBackgroundColor: markingState.setBackgroundColor,
        setPosition: markingState.setPosition,
        updateState: markingState.updateState,
        reset: markingState.reset,
        resetToCurrentData: markingState.resetToCurrentData,

        // Масштаб
        scale,
        handleScaleChange,
        getCurrentScale,

        // Сохранение и загрузка
        save,
        savedData: persistence.savedData,
        isLoading: persistence.isLoading,
        isFetching: persistence.isFetching,
        isSaving: persistence.isSaving,
    }
}
